// EmailCampaigns.jsx — BespokeBridge Communications Module
// Sub-tabs: Campaigns | Create/Edit | Templates | Campaign Frequency
// Props: { orgId, adminId, adminName }

import React, { useState, useEffect, useRef, useCallback } from 'react';
import CampaignFrequencyControl from './CampaignFrequencyControl';
import FinalCustomersTargeted from './FinalCustomersTargeted';
import MergeFieldValuesSection from './MergeFieldValuesSection';
import {
  getEmailCampaigns,
  createEmailCampaign,
  updateEmailCampaign,
  sendEmailCampaignNow,
  dispatchEmailCampaign,
  scheduleEmailCampaign,
  cancelEmailCampaign,
  duplicateEmailCampaign,
  getEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  seedDefaultTemplates,
  resolveAudience,
  resolveMergeFields,
  searchContacts,
  fetchDistinctFieldValues,
  getOrgName,
  getStaffDisplayName,
  MERGE_FIELDS,
  AUDIENCE_TYPES,
  MEMBER_STATUS_FILTERS,
  BIRTH_MONTHS,
  TARGETING_TYPES,
  fmtDate,
  fmtDateTime,
  getChannelOptInList,
  getContactOptIn,
  updateChannelOptIn,
  exportChannelOptInCSV,
} from '../../../api/communicationsApi';
import { getFinalContactList, recordCampaignDeliveries } from '../../../api/campaignExclusionService';

const PAGE_SIZE = 25;

const STATUS_STYLES = {
  draft:     { bg: '#F3F4F6', color: '#374151', border: '#D1D5DB' },
  scheduled: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  sent:      { bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7' },
  cancelled: { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' },
};

const COMMON_EMOJIS = ['😊', '🎉', '💙', '✨', '🌟', '🙌', '👋', '❤️', '🏆', '📢', '🗓️', '📋', '🤝', '💪', '🌸'];

// ── Recipient Preview Modal ───────────────────────────────────────────────────
function RecipientPreviewModal({ isOpen, onClose, previewData, campaignName }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText]   = useState('');
  const [sortField, setSortField]     = useState('displayName');
  const [sortDir, setSortDir]         = useState('asc');
  const perPage = 100;

  useEffect(() => { setCurrentPage(1); }, [filterText]);
  if (!isOpen || !previewData) return null;

  const { eligibleContacts = [], eligibleCount = 0, totalCount = 0 } = previewData;

  const filtered = eligibleContacts.filter(c => {
    if (!filterText) return true;
    const q = filterText.toLowerCase();
    return (c.displayName || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = (a[sortField] || '').toLowerCase();
    const bv = (b[sortField] || '').toLowerCase();
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const paged = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const handleExportCSV = () => {
    const rows = [['Name', 'Type', 'Email']];
    sorted.forEach(c => rows.push([`"${c.displayName || ''}"`, c._type || '', `"${c.email || ''}"`]));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `email-recipients-${(campaignName || 'campaign').replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${new Date().toISOString().slice(0,10)}.csv`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const coverage = totalCount > 0 ? ((eligibleCount / totalCount) * 100).toFixed(1) : '0.0';

  return (
    <div className="bb-comm-modal-overlay">
      <div className="bb-comm-modal bb-comm-modal--wide" style={{ maxWidth: 680 }}>
        <div className="bb-comm-modal-header">
          <h4 className="bb-comm-modal-title">Email Recipients Preview</h4>
          <button className="bb-comm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="bb-comm-modal-body">
          <div className="bb-comm-preview-stats">
            <div className="bb-comm-preview-stat">
              <div className="bb-comm-preview-stat-val">{eligibleCount}</div>
              <div className="bb-comm-preview-stat-lbl">Eligible Recipients</div>
            </div>
            <div className="bb-comm-preview-stat">
              <div className="bb-comm-preview-stat-val">{totalCount}</div>
              <div className="bb-comm-preview-stat-lbl">Total in Segment</div>
            </div>
            <div className="bb-comm-preview-stat">
              <div className="bb-comm-preview-stat-val" style={{ color: '#4338CA' }}>{coverage}%</div>
              <div className="bb-comm-preview-stat-lbl">Coverage</div>
            </div>
          </div>

          {eligibleCount > 0 ? (
            <>
              <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <input
                  className="bb-form-input"
                  style={{ flex: 1, minWidth: 200 }}
                  placeholder="Search by name or email…"
                  value={filterText}
                  onChange={e => setFilterText(e.target.value)}
                />
                {filterText && (
                  <button className="bb-btn-secondary" style={{ whiteSpace: 'nowrap' }} onClick={() => setFilterText('')}>Clear</button>
                )}
                <button className="bb-btn-secondary" onClick={handleExportCSV}>📥 Export CSV</button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="bb-table" style={{ width: '100%', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('displayName')} style={{ cursor: 'pointer' }}>
                        Name {sortField === 'displayName' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                      </th>
                      <th>Type</th>
                      <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                        Email {sortField === 'email' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.length === 0 ? (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: '#9CA3AF', padding: 20 }}>No results match your search.</td></tr>
                    ) : paged.map((c, i) => (
                      <tr key={c.id || i}>
                        <td style={{ fontWeight: 600 }}>{c.displayName || '—'}</td>
                        <td style={{ textTransform: 'capitalize', fontSize: 11, color: '#6B7280' }}>{c._type}</td>
                        <td style={{ color: '#4338CA' }}>{c.email || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 12, fontSize: 13 }}>
                  <button className="bb-btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                  <span style={{ color: '#6B7280' }}>Page {currentPage} of {totalPages}</span>
                  <button className="bb-btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '30px 20px', color: '#9CA3AF' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
              <div>No eligible recipients found for the selected criteria.</div>
            </div>
          )}
        </div>
        <div className="bb-comm-modal-footer">
          <button className="bb-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Status & Audience helpers ─────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, border: `1px solid ${s.border}`, background: s.bg, color: s.color, textTransform: 'capitalize' }}>
      {status || 'draft'}
    </span>
  );
}

function AudienceLabel({ audienceType }) {
  const map = { members: 'Members', volunteers: 'Volunteers', both: 'Members + Volunteers', donors: 'Donors', all: 'Members + Volunteers + Donors' };
  return <span>{map[audienceType] || audienceType || '—'}</span>;
}

// ── Emoji Picker ──────────────────────────────────────────────────────────────
function EmojiPicker({ onSelect }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" className="bb-btn-secondary" style={{ padding: '5px 10px', fontSize: 16 }} onClick={() => setOpen(o => !o)}>
        😊
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', left: 0, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 10, zIndex: 200, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', display: 'flex', flexWrap: 'wrap', gap: 4, width: 220 }}>
          {COMMON_EMOJIS.map(e => (
            <button key={e} type="button" onClick={() => { onSelect(e); setOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, padding: 4, borderRadius: 6, lineHeight: 1 }}
              onMouseOver={ev => ev.target.style.background = '#F3F4F6'}
              onMouseOut={ev => ev.target.style.background = 'none'}
            >
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Audience Filters ──────────────────────────────────────────────────────────
// Which targeting options belong to which audience family
const MEMBER_TARGETING    = ['all_members', 'member_status', 'member_district', 'member_birthday'];
const VOLUNTEER_TARGETING = ['all_volunteers', 'volunteer_status', 'volunteer_district'];
const DONOR_TARGETING     = ['all_donors'];
const SHARED_TARGETING    = ['all_contacts', 'specific_contacts'];

function filterTargetingTypes(templateAudience) {
  if (!templateAudience || templateAudience === 'both' || templateAudience === 'all') return TARGETING_TYPES;
  if (templateAudience === 'donors')
    return TARGETING_TYPES.filter(t => DONOR_TARGETING.includes(t.value) || SHARED_TARGETING.includes(t.value));
  if (templateAudience === 'volunteers')
    return TARGETING_TYPES.filter(t => VOLUNTEER_TARGETING.includes(t.value) || SHARED_TARGETING.includes(t.value));
  // 'members' (default)
  return TARGETING_TYPES.filter(t => MEMBER_TARGETING.includes(t.value) || SHARED_TARGETING.includes(t.value));
}

function AudienceFiltersSection({
  targetingType, onTargetingTypeChange,
  audienceFilters, onFiltersChange,
  contactSearch, setContactSearch,
  contactResults, searchingContacts,
  selectedContacts, onAddContact, onRemoveContact,
  memberStatuses, memberDistricts,
  volunteerStatuses, volunteerDistricts,
  templateAudience,
}) {
  const [contactInputFocused, setContactInputFocused] = React.useState(false);
  const availableTargetingTypes = filterTargetingTypes(templateAudience);
  const toggleBirthMonth = (month) => {
    const cur = audienceFilters.birthMonths || [];
    onFiltersChange({ ...audienceFilters, birthMonths: cur.includes(month) ? cur.filter(m => m !== month) : [...cur, month] });
  };

  const toggleStatus = (field, val) => {
    const cur = audienceFilters[field] || [];
    onFiltersChange({ ...audienceFilters, [field]: cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val] });
  };

  return (
    <div>
      <div className="bb-form-group">
        <label className="bb-form-label">Targeting Type</label>
        <select className="bb-form-select" value={targetingType} onChange={e => { onTargetingTypeChange(e.target.value); onFiltersChange({}); }}>
          {availableTargetingTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* Member status sub-filter */}
      {targetingType === 'member_status' && (
        <div className="bb-form-group">
          <label className="bb-form-label">Member Status</label>
          <div className="bb-comm-checkbox-group">
            {(memberStatuses.length > 0 ? memberStatuses : MEMBER_STATUS_FILTERS.map(f => f.value)).map(val => (
              <label key={val} className="bb-comm-checkbox-label">
                <input type="checkbox" checked={(audienceFilters.memberStatus || []).includes(val)} onChange={() => toggleStatus('memberStatus', val)} />
                {val}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Leave all unchecked to include all member statuses.</div>
        </div>
      )}

      {/* Member district sub-filter */}
      {targetingType === 'member_district' && (
        <div className="bb-form-group">
          <label className="bb-form-label">Member District</label>
          <select
            className="bb-form-select"
            style={{ maxWidth: 280 }}
            value={audienceFilters.memberDistrict || ''}
            onChange={e => onFiltersChange({ ...audienceFilters, memberDistrict: e.target.value })}
          >
            <option value="">— Select district —</option>
            {memberDistricts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      )}

      {/* Birthday month sub-filter */}
      {targetingType === 'member_birthday' && (
        <div className="bb-form-group">
          <label className="bb-form-label">Birthday Month(s)</label>
          <div className="bb-comm-checkbox-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
            {BIRTH_MONTHS.map(m => (
              <label key={m} className="bb-comm-checkbox-label">
                <input type="checkbox" checked={(audienceFilters.birthMonths || []).includes(m)} onChange={() => toggleBirthMonth(m)} />
                {m}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Volunteer status sub-filter */}
      {targetingType === 'volunteer_status' && (
        <div className="bb-form-group">
          <label className="bb-form-label">Volunteer Status</label>
          <div className="bb-comm-checkbox-group">
            {(volunteerStatuses.length > 0 ? volunteerStatuses : ['active', 'inactive']).map(val => (
              <label key={val} className="bb-comm-checkbox-label">
                <input type="checkbox" checked={(audienceFilters.volunteerStatus || []).includes(val)} onChange={() => toggleStatus('volunteerStatus', val)} />
                {val}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Leave all unchecked to include all volunteer statuses.</div>
        </div>
      )}

      {/* Volunteer district sub-filter */}
      {targetingType === 'volunteer_district' && (
        <div className="bb-form-group">
          <label className="bb-form-label">Volunteer District</label>
          <select
            className="bb-form-select"
            style={{ maxWidth: 280 }}
            value={audienceFilters.volunteerDistrict || ''}
            onChange={e => onFiltersChange({ ...audienceFilters, volunteerDistrict: e.target.value })}
          >
            <option value="">— Select district —</option>
            {volunteerDistricts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      )}

      {/* Specific contacts sub-filter */}
      {targetingType === 'specific_contacts' && (
        <div className="bb-form-group">
          <label className="bb-form-label">Search & Select Contacts</label>
          <div style={{ position: 'relative' }}>
            <input
              className="bb-form-input"
              placeholder="Search by name or email…"
              value={contactSearch}
              onChange={e => setContactSearch(e.target.value)}
              onFocus={() => setContactInputFocused(true)}
              onBlur={() => setTimeout(() => setContactInputFocused(false), 150)}
            />
            {searchingContacts && (
              <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#9CA3AF' }}>Searching…</div>
            )}
            {contactInputFocused && contactResults.length > 0 && (
              <div style={{ position: 'absolute', top: '105%', left: 0, right: 0, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, zIndex: 100, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                {contactResults.map(c => (
                  <div
                    key={c.id}
                    onClick={() => onAddContact(c)}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}
                    onMouseOver={e => e.currentTarget.style.background = '#F9FAFB'}
                    onMouseOut={e => e.currentTarget.style.background = '#fff'}
                  >
                    <span style={{ fontWeight: 600 }}>{c.displayName}</span>
                    <span style={{ fontSize: 11, color: '#6B7280', marginLeft: 6, textTransform: 'capitalize' }}>({c._type})</span>
                    {c.email && <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 6 }}>{c.email}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          {selectedContacts.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {selectedContacts.map(c => (
                <span key={c.id} style={{ background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE', borderRadius: 20, padding: '3px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {c.displayName}
                  <button type="button" onClick={() => onRemoveContact(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4338CA', padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                </span>
              ))}
            </div>
          )}
          {selectedContacts.length === 0 && (
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>No contacts selected yet. Search above to add recipients.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Send Confirmation Modal ───────────────────────────────────────────────────
function SendConfirmModal({ name, subject, recipientCount, scheduleMode, scheduleAt, onConfirm, onClose, sending }) {
  return (
    <div className="bb-comm-modal-overlay">
      <div className="bb-comm-modal">
        <div className="bb-comm-modal-header">
          <h4 className="bb-comm-modal-title">{scheduleMode ? 'Schedule Campaign' : 'Send Campaign Now'}</h4>
          <button className="bb-comm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="bb-comm-modal-body">
          <div className="bb-comm-detail-grid">
            {[
              ['Campaign',   name],
              ['Subject',    subject],
              ['Recipients', <span style={{ fontWeight: 700, color: '#4338CA', fontSize: 16 }}>{recipientCount}</span>],
              [scheduleMode ? 'Scheduled For' : 'Send Time',
                scheduleMode && scheduleAt
                  ? new Date(scheduleAt).toLocaleString('en-SG', { timeZone: 'Asia/Singapore', dateStyle: 'medium', timeStyle: 'short' })
                  : 'Immediately'],
            ].map(([lbl, val]) => (
              <div key={lbl} className="bb-comm-detail-row">
                <span className="bb-comm-detail-label">{lbl}</span>
                <span style={{ color: '#374151', fontSize: 13 }}>{val}</span>
              </div>
            ))}
          </div>
          {recipientCount === 0 && (
            <div className="bb-comm-warning-box">No eligible recipients found. Check your audience filters before sending.</div>
          )}
        </div>
        <div className="bb-comm-modal-footer">
          <button className="bb-btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="bb-btn-primary" onClick={onConfirm} disabled={sending || recipientCount === 0}>
            {sending ? 'Working…' : scheduleMode ? 'Schedule' : 'Send Now'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── View Campaign Modal ───────────────────────────────────────────────────────
function ViewModal({ campaign, onClose }) {
  if (!campaign) return null;
  return (
    <div className="bb-comm-modal-overlay">
      <div className="bb-comm-modal bb-comm-modal--wide">
        <div className="bb-comm-modal-header">
          <h4 className="bb-comm-modal-title">Campaign Details</h4>
          <button className="bb-comm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="bb-comm-modal-body">
          <div className="bb-comm-detail-grid">
            {[
              ['Name',       campaign.name],
              ['Status',     <StatusBadge status={campaign.status} />],
              ['Subject',    campaign.subject || '—'],
              ['Audience',   <AudienceLabel audienceType={campaign.audienceType} />],
              ['Recipients', campaign.recipientCount || '—'],
              ['Scheduled',  fmtDateTime(campaign.scheduledAt)],
              ['Sent',       fmtDateTime(campaign.sentAt)],
              ['Created By', campaign.createdBy || '—'],
              ['Created',    fmtDate(campaign.createdAt)],
            ].map(([lbl, val]) => (
              <div key={lbl} className="bb-comm-detail-row">
                <span className="bb-comm-detail-label">{lbl}</span>
                <span style={{ color: '#374151', fontSize: 13 }}>{val}</span>
              </div>
            ))}
          </div>
          {campaign.body && (
            <div className="bb-comm-detail-body">
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Body</div>
              <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '14px 16px', fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {campaign.body}
              </div>
            </div>
          )}
        </div>
        <div className="bb-comm-modal-footer">
          <button className="bb-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Template Editor Form ──────────────────────────────────────────────────────
function TemplateEditorForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    name:           initial?.name           || '',
    category:       initial?.category       || 'triggered',
    triggerContext: initial?.triggerContext || '',
    audience:       initial?.audience       || 'members',
    subject:        initial?.subject        || '',
    body:           initial?.body           || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const bodyRef = useRef(null);

  const insertMergeField = (tag) => {
    if (bodyRef.current) {
      const el = bodyRef.current;
      const s = el.selectionStart, e = el.selectionEnd;
      const next = form.body.slice(0, s) + tag + form.body.slice(e);
      set('body', next);
      setTimeout(() => { el.selectionStart = el.selectionEnd = s + tag.length; el.focus(); }, 0);
    } else {
      set('body', form.body + tag);
    }
  };

  return (
    <div className="bb-comm-template-editor">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="bb-form-group">
          <label className="bb-form-label">Template Name *</label>
          <input className="bb-form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Welcome to BespokeBridge" />
        </div>
        <div className="bb-form-group">
          <label className="bb-form-label">Campaign Type</label>
          <select className="bb-form-select" value={form.category} onChange={e => set('category', e.target.value)}>
            <option value="triggered">Triggered Campaign</option>
            <option value="adhoc">AdHoc Campaign</option>
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="bb-form-group">
          <label className="bb-form-label">Trigger Context</label>
          <input className="bb-form-input" value={form.triggerContext} onChange={e => set('triggerContext', e.target.value)} placeholder="e.g. New member onboarding" />
        </div>
        <div className="bb-form-group">
          <label className="bb-form-label">Audience</label>
          <select className="bb-form-select" value={form.audience} onChange={e => set('audience', e.target.value)}>
            {AUDIENCE_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
      </div>
      <div className="bb-form-group">
        <label className="bb-form-label">Subject Line *</label>
        <input className="bb-form-input" value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="e.g. Welcome to {{orgName}}!" />
      </div>
      <div className="bb-form-group">
        <label className="bb-form-label">Body *</label>
        <div className="bb-comm-merge-fields" style={{ marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9CA3AF', marginRight: 4 }}>Insert:</span>
          {MERGE_FIELDS.filter(f => f.tag === '{{firstName}}' || f.tag === '{{fullName}}').map(f => (
            <button key={f.tag} className="bb-comm-merge-btn" type="button" onClick={() => insertMergeField(f.tag)}>{f.label}</button>
          ))}
        </div>
        <textarea
          ref={bodyRef}
          className="bb-form-input"
          style={{ minHeight: 140, fontFamily: 'inherit', fontSize: 13 }}
          value={form.body}
          onChange={e => set('body', e.target.value)}
          placeholder="Write your email body here…"
        />
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="bb-btn-primary" onClick={() => onSave(form)} disabled={saving || !form.name || !form.subject || !form.body}>
          {saving ? 'Saving…' : 'Save Template'}
        </button>
        <button className="bb-btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

// ── Recurring Schedule section ────────────────────────────────────────────────
function RecurringSection({ recurring, onChange }) {
  const set = (k, v) => onChange({ ...recurring, [k]: v });
  return (
    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 14, marginTop: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
        <div className="bb-form-group" style={{ marginBottom: 0 }}>
          <label className="bb-form-label">Frequency</label>
          <select className="bb-form-select" value={recurring.frequency || 'weekly'} onChange={e => set('frequency', e.target.value)}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="annually">Annually</option>
          </select>
        </div>
        {recurring.frequency === 'weekly' && (
          <div className="bb-form-group" style={{ marginBottom: 0 }}>
            <label className="bb-form-label">Day of Week</label>
            <select className="bb-form-select" value={recurring.dayOfWeek || 'Monday'} onChange={e => set('dayOfWeek', e.target.value)}>
              {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        )}
        {recurring.frequency === 'monthly' && (
          <div className="bb-form-group" style={{ marginBottom: 0 }}>
            <label className="bb-form-label">Day of Month</label>
            <input type="number" className="bb-form-input" min={1} max={28} value={recurring.dayOfMonth || 1} onChange={e => set('dayOfMonth', parseInt(e.target.value, 10))} />
          </div>
        )}
        {recurring.frequency === 'annually' && (
          <div className="bb-form-group" style={{ marginBottom: 0 }}>
            <label className="bb-form-label">Month</label>
            <select className="bb-form-select" value={recurring.monthOfYear || 'January'} onChange={e => set('monthOfYear', e.target.value)}>
              {BIRTH_MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="bb-form-group" style={{ marginBottom: 0 }}>
          <label className="bb-form-label">Start Date *</label>
          <input type="date" className="bb-form-input" value={recurring.startDate || ''} onChange={e => set('startDate', e.target.value)} />
        </div>
        <div className="bb-form-group" style={{ marginBottom: 0 }}>
          <label className="bb-form-label">End Date</label>
          <input type="date" className="bb-form-input" value={recurring.endDate || ''} onChange={e => set('endDate', e.target.value)} />
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8 }}>
        Leave end date blank for an ongoing recurring campaign.
      </div>
    </div>
  );
}

// ── Message Preview Pane (mobile / desktop toggle) ───────────────────────────
function MessagePreviewPane({ subject, body, orgName, previewName, customMergeValues }) {
  const [device, setDevice] = React.useState('mobile');
  const hasContent = !!(subject || body);
  if (!hasContent) return null;

  // Build a contact-shaped object so resolveMergeFields can split displayName → firstName
  const sampleContact = { displayName: previewName || 'Staff Member' };
  const resolvedOrgName = orgName || '[Org Name]';

  const resolvedSubject = resolveMergeFields(subject, sampleContact, resolvedOrgName, customMergeValues);
  const resolvedBody = resolveMergeFields(body || '', sampleContact, resolvedOrgName, customMergeValues)
    .replace(/\[IMAGE:(.*?)\]/g, (_, url) => `<img src="${url.trim()}" style="max-width:100%;height:auto;display:block;border-radius:4px;margin:6px 0;" />`);
  const isHtml = /<[a-z]/i.test(resolvedBody);

  const btnStyle = (key) => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px',
    borderRadius: 20, fontSize: 12, cursor: 'pointer', transition: 'all 0.15s',
    border: `1.5px solid ${device === key ? '#4338CA' : '#D1D5DB'}`,
    background: device === key ? '#EEF2FF' : '#FAFAFA',
    color: device === key ? '#4338CA' : '#9CA3AF',
    fontWeight: device === key ? 700 : 400,
  });

  return (
    <div className="bb-comm-preview-panel" style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span className="bb-comm-preview-label" style={{ margin: 0 }}>Message Preview</span>
        <button type="button" style={btnStyle('mobile')} onClick={() => setDevice('mobile')}>
          <svg width="10" height="14" viewBox="0 0 10 14" fill="none"><rect x=".5" y=".5" width="9" height="13" rx="1.5" stroke="currentColor" strokeWidth="1"/><rect x="3.5" y="11" width="3" height="1" rx=".5" fill="currentColor"/></svg>
          Mobile
        </button>
        <button type="button" style={btnStyle('desktop')} onClick={() => setDevice('desktop')}>
          <svg width="14" height="11" viewBox="0 0 14 11" fill="none"><rect x=".5" y=".5" width="13" height="8" rx="1" stroke="currentColor" strokeWidth="1"/><rect x="2" y="2" width="10" height="5.5" rx=".3" fill="currentColor" opacity=".18"/><rect x="0" y="9" width="14" height="1.5" rx=".75" fill="currentColor" opacity=".5"/></svg>
          Desktop
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
        {device === 'mobile' ? (
          <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
            <div style={{ position: 'absolute', left: -3, top: 58, width: 3, height: 11, background: '#3a3a3a', borderRadius: '2px 0 0 2px' }} />
            <div style={{ position: 'absolute', left: -3, top: 76, width: 3, height: 20, background: '#3a3a3a', borderRadius: '2px 0 0 2px' }} />
            <div style={{ position: 'absolute', left: -3, top: 102, width: 3, height: 20, background: '#3a3a3a', borderRadius: '2px 0 0 2px' }} />
            <div style={{ position: 'absolute', right: -3, top: 82, width: 3, height: 28, background: '#3a3a3a', borderRadius: '0 2px 2px 0' }} />
            <div style={{ width: 224, background: 'linear-gradient(170deg,#2d2d2d 0%,#1a1a1a 50%,#232323 100%)', borderRadius: 22, overflow: 'hidden', boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 22px 55px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.1)' }}>
              <div style={{ background: '#000', paddingTop: 10, paddingBottom: 5, display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: 50, height: 14, background: '#0d0d0d', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 5 }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#151515' }} />
                </div>
              </div>
              <div style={{ height: 362, overflowY: 'auto', overflowX: 'hidden', background: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                <div style={{ background: '#f2f2f7', padding: '8px 8px 6px', borderBottom: '1px solid #d1d1d6', position: 'sticky', top: 0, zIndex: 1 }}>
                  <div style={{ fontSize: 8, color: '#007aff', marginBottom: 3 }}>← Inbox</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#000', lineHeight: 1.25, wordBreak: 'break-word' }}>
                    {resolvedSubject || <span style={{ fontStyle: 'italic', fontWeight: 400, color: '#bbb' }}>No subject</span>}
                  </div>
                  <div style={{ fontSize: 7.5, color: '#888', marginTop: 3 }}>BespokeBridge · now</div>
                </div>
                <div style={{ padding: '8px 8px' }}>
                  {resolvedBody
                    ? isHtml
                      ? <div style={{ fontSize: 8, color: '#333', lineHeight: 1.5, wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: resolvedBody }} />
                      : <div style={{ fontSize: 8.5, color: '#333', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{resolvedBody}</div>
                    : <div style={{ fontSize: 8.5, color: '#bbb', fontStyle: 'italic' }}>Email body will appear here…</div>
                  }
                </div>
              </div>
              <div style={{ background: '#000', paddingTop: 6, paddingBottom: 12, display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: 40, height: 4, background: 'rgba(255,255,255,0.22)', borderRadius: 3 }} />
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'inline-block', flexShrink: 0 }}>
            <div style={{ width: 483, background: '#1c1c1e', borderRadius: '8px 8px 0 0', padding: '8px 7px 0', boxSizing: 'border-box' }}>
              <div style={{ width: 4, height: 4, background: '#444', borderRadius: '50%', margin: '0 auto 4px' }} />
              <div style={{ background: '#fff', borderRadius: '3px 3px 0 0', height: 276, overflowY: 'auto', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                <div style={{ background: '#f5f5f5', borderBottom: '1px solid #e0e0e0', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6, position: 'sticky', top: 0, zIndex: 1 }}>
                  <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#ff5f57', flexShrink: 0 }} />
                  <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#febc2e', flexShrink: 0 }} />
                  <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#28c840', flexShrink: 0 }} />
                  <div style={{ flex: 1, marginLeft: 8, fontSize: 8.5, color: '#666', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontWeight: 500 }}>
                    {resolvedSubject || <span style={{ color: '#bbb' }}>No subject</span>}
                  </div>
                </div>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', marginBottom: 5, wordBreak: 'break-word' }}>
                    {resolvedSubject || <span style={{ fontStyle: 'italic', fontWeight: 400, color: '#bbb', fontSize: 12 }}>Subject line will appear here…</span>}
                  </div>
                  <div style={{ fontSize: 8.5, color: '#888' }}>BespokeBridge · now</div>
                </div>
                <div style={{ flex: 1, padding: '12px 16px' }}>
                  {resolvedBody
                    ? isHtml
                      ? <div style={{ fontSize: 11, color: '#333', lineHeight: 1.6, wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: resolvedBody }} />
                      : <div style={{ fontSize: 11, color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{resolvedBody}</div>
                    : <div style={{ fontSize: 11, color: '#bbb', fontStyle: 'italic' }}>Email body will appear here…</div>
                  }
                </div>
              </div>
            </div>
            <div style={{ width: 483, height: 10, background: '#2c2c2e' }} />
            <div style={{ width: 515, height: 8, background: '#1c1c1e', borderRadius: '0 0 6px 6px', marginLeft: -16, boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }} />
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 10, textAlign: 'center' }}>
        Preview using your name and organisation. {device === 'mobile' ? 'Mobile' : 'Desktop'} view.
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function EmailCampaigns({ orgId, adminId, adminName }) {
  const [activeTab, setActiveTab] = useState('create');

  // ── Campaigns tab ─────────────────────────────────────────────────────────
  const [campaigns, setCampaigns]           = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [statusFilter, setStatusFilter]     = useState('');
  const [search, setSearch]                 = useState('');
  const [page, setPage]                     = useState(1);
  const [viewCampaign, setViewCampaign]     = useState(null);
  const [toast, setToast]                   = useState(null);

  // ── Create/Edit tab ───────────────────────────────────────────────────────
  const [editingId, setEditingId]           = useState(null);
  const [templates, setTemplates]           = useState([]);
  const [form, setForm]                     = useState(emptyForm());
  const [targetingType, setTargetingType]   = useState('all_members');
  const [templateAudience, setTemplateAudience] = useState(null); // null = no template selected
  const [audienceResult, setAudienceResult] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [savedTargeting, setSavedTargeting] = useState(null);
  const [formSaving, setFormSaving]         = useState(false);
  const [showRecipientPreview, setShowRecipientPreview] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sendAction, setSendAction]         = useState('now');
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const bodyRef = useRef(null);
  const [showImageInsert, setShowImageInsert] = useState(false);
  const [imageInsertUrl, setImageInsertUrl]   = useState('');

  // ── Specific contact search ───────────────────────────────────────────────
  const [contactSearch, setContactSearch]     = useState('');
  const [contactResults, setContactResults]   = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [searchingContacts, setSearchingContacts] = useState(false);

  // ── Preview personalisation values ───────────────────────────────────────
  const [previewOrgName, setPreviewOrgName]   = useState('');
  const [previewStaffName, setPreviewStaffName] = useState(adminName || '');

  // ── Dynamic targeting values ──────────────────────────────────────────────
  const [memberStatuses, setMemberStatuses]       = useState([]);
  const [memberDistricts, setMemberDistricts]     = useState([]);
  const [volunteerStatuses, setVolunteerStatuses] = useState([]);
  const [volunteerDistricts, setVolunteerDistricts] = useState([]);

  // ── Opt-In Management tab ────────────────────────────────────────────────
  const [optInContacts, setOptInContacts]     = useState([]);
  const [loadingOptIn, setLoadingOptIn]       = useState(false);
  const [optInFilter, setOptInFilter]         = useState('');
  const [optInSearch, setOptInSearch]         = useState('');
  const [optInPage, setOptInPage]             = useState(1);
  const [updatingOptIn, setUpdatingOptIn]     = useState(null);
  const [optInReason, setOptInReason]         = useState('');
  const [showOptInReasonFor, setShowOptInReasonFor] = useState(null);

  // ── Templates tab ─────────────────────────────────────────────────────────
  const [tplCategory, setTplCategory]       = useState('all');
  const [tplEditingId, setTplEditingId]     = useState(null);
  const [tplSaving, setTplSaving]           = useState(false);
  const [tplDeleteConfirm, setTplDeleteConfirm] = useState(null);
  const [tplDeleting, setTplDeleting]       = useState(false);

  function emptyForm() {
    return {
      name: '', templateId: '', subject: '', body: '',
      audienceType: 'members', audienceFilters: {},
      scheduleType: 'now', scheduledAt: '',
      isRecurring: false,
      recurring: { frequency: 'weekly', dayOfWeek: 'Monday', dayOfMonth: 1, monthOfYear: 'January', startDate: '', endDate: '' },
      customMergeValues: {},
    };
  }

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Contact search ────────────────────────────────────────────────────────
  useEffect(() => {
    if (targetingType !== 'specific_contacts') return;
    const timer = setTimeout(async () => {
      setSearchingContacts(true);
      try {
        const results = await searchContacts(orgId, contactSearch, 'both');
        setContactResults(results.filter(c => !selectedContacts.find(s => s.id === c.id)));
      } catch (err) {
        console.error('searchContacts:', err);
      } finally {
        setSearchingContacts(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearch, orgId, targetingType, selectedContacts]);

  const handleAddContact = (c) => {
    setSelectedContacts(prev => [...prev, c]);
    setContactSearch('');
    setContactResults([]);
    setF('audienceFilters', { ...form.audienceFilters, specificContactIds: [...(form.audienceFilters.specificContactIds || []), c.id] });
  };

  const handleRemoveContact = (id) => {
    setSelectedContacts(prev => prev.filter(c => c.id !== id));
    setF('audienceFilters', { ...form.audienceFilters, specificContactIds: (form.audienceFilters.specificContactIds || []).filter(x => x !== id) });
  };

  // ── Derive audienceType from targetingType ────────────────────────────────
  const derivedAudienceType = () => {
    if (['all_volunteers', 'volunteer_district', 'volunteer_status'].includes(targetingType)) return 'volunteers';
    if (targetingType === 'all_donors') return 'donors';
    if (targetingType === 'all_contacts') return 'all';
    if (targetingType === 'specific_contacts') return 'both';
    return 'members';
  };

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      setCampaigns(await getEmailCampaigns(orgId));
    } catch (err) {
      showToast('Failed to load campaigns: ' + err.message, 'error');
    } finally {
      setLoadingCampaigns(false);
    }
  }, [orgId]);

  const loadTemplates = useCallback(async () => {
    try {
      setTemplates(await getEmailTemplates(orgId));
    } catch (err) {
      console.error('loadTemplates:', err);
    }
  }, [orgId]);

  const loadOptInContacts = useCallback(async () => {
    setLoadingOptIn(true);
    try {
      setOptInContacts(await getChannelOptInList(orgId));
    } catch (err) {
      showToast('Failed to load opt-in list: ' + err.message, 'error');
    } finally {
      setLoadingOptIn(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (activeTab === 'optin') loadOptInContacts();
  }, [activeTab, loadOptInContacts]);

  useEffect(() => {
    if (!orgId) return;
    seedDefaultTemplates(orgId).then(() => {
      loadCampaigns();
      loadTemplates();
    });
    // Load dynamic field values for targeting dropdowns/checkboxes
    fetchDistinctFieldValues('members', orgId, 'status').then(v => setMemberStatuses(v));
    fetchDistinctFieldValues('members', orgId, 'district').then(v => setMemberDistricts(v));
    fetchDistinctFieldValues('volunteers', orgId, 'status').then(v => setVolunteerStatuses(v));
    fetchDistinctFieldValues('volunteers', orgId, 'district').then(v => setVolunteerDistricts(v));
    // Load preview personalisation: org name from Firestore, staff name from staff collection
    getOrgName(orgId).then(n => setPreviewOrgName(n));
    if (adminId) {
      getStaffDisplayName(orgId, adminId).then(n => { if (n) setPreviewStaffName(n); });
    }
  }, [orgId, loadCampaigns, loadTemplates]);

  // ── Audience count ────────────────────────────────────────────────────────
  const refreshAudience = useCallback(async (audType, audFilters) => {
    if (!orgId) return;
    setAudienceLoading(true);
    try {
      const r = await resolveAudience(orgId, audType, audFilters, 'email');
      setAudienceResult(r);
    } catch (err) {
      console.error('resolveAudience:', err);
    } finally {
      setAudienceLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (activeTab === 'create') {
      const audType = derivedAudienceType();
      setF('audienceType', audType);
      refreshAudience(audType, form.audienceFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, targetingType, JSON.stringify(form.audienceFilters)]);

  // ── Template auto-fill ────────────────────────────────────────────────────
  const handleTemplateSelect = (templateId) => {
    const tpl = templates.find(t => t.id === templateId);
    if (tpl) {
      const aud = tpl.audience || 'members';
      setForm(f => ({
        ...f,
        templateId,
        name: f.name || tpl.name || '',
        subject: tpl.subject || f.subject,
        body: tpl.body || f.body,
      }));
      setTemplateAudience(aud);
      // Reset targeting type to the most general option for this audience
      if (aud === 'volunteers') setTargetingType('all_volunteers');
      else if (aud === 'both' || aud === 'all') setTargetingType('all_contacts');
      else if (aud === 'donors') setTargetingType('all_donors');
      else setTargetingType('all_members');
    } else {
      // Template cleared — lift all restrictions
      setF('templateId', '');
      setTemplateAudience(null);
    }
  };

  // ── Merge field insert ────────────────────────────────────────────────────
  const insertMergeField = (tag) => {
    if (bodyRef.current) {
      const el = bodyRef.current;
      const s = el.selectionStart, e = el.selectionEnd;
      const next = form.body.slice(0, s) + tag + form.body.slice(e);
      setF('body', next);
      setTimeout(() => { el.selectionStart = el.selectionEnd = s + tag.length; el.focus(); }, 0);
    } else {
      setF('body', form.body + tag);
    }
  };

  // ── Image insert ──────────────────────────────────────────────────────────
  const handleInsertImage = () => {
    const url = imageInsertUrl.trim();
    if (!url) return;
    const tag = `[IMAGE:${url}]`;
    if (bodyRef.current) {
      const el = bodyRef.current;
      const s = el.selectionStart, e = el.selectionEnd;
      const next = form.body.slice(0, s) + tag + form.body.slice(e);
      setF('body', next);
      setTimeout(() => { el.selectionStart = el.selectionEnd = s + tag.length; el.focus(); }, 0);
    } else {
      setF('body', form.body + tag);
    }
    setImageInsertUrl('');
    setShowImageInsert(false);
  };

  // ── Save draft ────────────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (!form.name.trim()) { showToast('Campaign name is required.', 'error'); return; }
    setFormSaving(true);
    try {
      const audType = derivedAudienceType();
      const payload = { name: form.name, templateId: form.templateId, subject: form.subject, body: form.body, audienceType: audType, audienceFilters: form.audienceFilters, customMergeValues: form.customMergeValues || {} };
      if (editingId) {
        await updateEmailCampaign(orgId, editingId, payload);
      } else {
        const newId = await createEmailCampaign(orgId, payload, adminId);
        setEditingId(newId);
      }
      showToast('Draft saved.');
      loadCampaigns();
    } catch (err) {
      showToast('Save failed: ' + err.message, 'error');
    } finally {
      setFormSaving(false);
    }
  };

  // ── Open send confirm ─────────────────────────────────────────────────────
  const handleOpenSend = async (action) => {
    if (!form.name || !form.subject || !form.body) { showToast('Name, subject, and body are required.', 'error'); return; }
    if (action === 'schedule' && !form.scheduledAt) { showToast('Please select a scheduled date/time.', 'error'); return; }
    const audType = derivedAudienceType();
    await refreshAudience(audType, form.audienceFilters);
    setSendAction(action);
    setShowSendConfirm(true);
  };

  // ── Confirm send ──────────────────────────────────────────────────────────
  const handleConfirmSend = async () => {
    setSendingCampaign(true);
    try {
      const audType = derivedAudienceType();
      const payload = { name: form.name, templateId: form.templateId, subject: form.subject, body: form.body, audienceType: audType, audienceFilters: form.audienceFilters, customMergeValues: form.customMergeValues || {} };
      let cId = editingId;
      if (!cId) {
        cId = await createEmailCampaign(orgId, payload, adminId);
        setEditingId(cId);
      } else {
        await updateEmailCampaign(orgId, cId, payload);
      }
      const eligible = audienceResult?.eligibleCount || 0;
      if (sendAction === 'now') {
        // Same "final list" the admin previewed: opted-in, within frequency limits.
        const { eligibleCustomers } = await getFinalContactList(
          orgId, 'email', { audienceType: audType, audienceFilters: form.audienceFilters }, null
        );
        if (eligibleCustomers.length === 0) {
          throw new Error('No eligible recipients (everyone targeted is unsubscribed, has no email, or is over the frequency limit).');
        }
        const orgName = previewOrgName || await getOrgName(orgId);
        const { queued, failedToQueue } = await dispatchEmailCampaign(orgId, cId, {
          subject: form.subject, body: form.body, orgName, contacts: eligibleCustomers, customMergeValues: form.customMergeValues || {},
        });
        if (queued === 0) throw new Error('No emails could be queued. Check you have permission to send email.');
        await sendEmailCampaignNow(orgId, cId, queued, adminId);
        await recordCampaignDeliveries(orgId, 'email', eligibleCustomers);
        showToast(failedToQueue > 0
          ? `Sending to ${queued} recipient(s); ${failedToQueue} could not be queued.`
          : `Sending to ${queued} recipient(s).`);
      } else {
        await scheduleEmailCampaign(orgId, cId, form.scheduledAt, eligible, adminId);
        // Delivered by the processScheduledEmailCampaigns Cloud Function (runs every 5 minutes).
        showToast('Campaign scheduled. It will be sent within about 5 minutes of the chosen time.');
      }
      setShowSendConfirm(false);
      resetForm();
      await loadCampaigns();
      setActiveTab('create');
    } catch (err) {
      showToast('Failed: ' + err.message, 'error');
    } finally {
      setSendingCampaign(false);
    }
  };

  // ── Table actions ─────────────────────────────────────────────────────────
  const handleEdit = (c) => {
    setForm({ name: c.name || '', templateId: c.templateId || '', subject: c.subject || '', body: c.body || '', audienceType: c.audienceType || 'members', audienceFilters: c.audienceFilters || {}, scheduleType: 'now', scheduledAt: '', isRecurring: false, recurring: { frequency: 'weekly', dayOfWeek: 'Monday', dayOfMonth: 1, monthOfYear: 'January', startDate: '', endDate: '' }, customMergeValues: c.customMergeValues || {} });
    setEditingId(c.id);
    setAudienceResult(null);
    setSelectedContacts([]);
    setTargetingType(c.audienceType === 'volunteers' ? 'all_volunteers' : c.audienceType === 'donors' ? 'all_donors' : (c.audienceType === 'both' || c.audienceType === 'all') ? 'all_contacts' : 'all_members');
    setActiveTab('create');
  };

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId(null);
    setAudienceResult(null);
    setSavedTargeting(null);
    setSelectedContacts([]);
    setContactSearch('');
    setContactResults([]);
    setTargetingType('all_members');
    setTemplateAudience(null);
  };

  const handleDuplicate = async (c) => {
    try {
      await duplicateEmailCampaign(orgId, c.id, adminId);
      showToast('Campaign duplicated.');
      loadCampaigns();
    } catch (err) {
      showToast('Duplicate failed: ' + err.message, 'error');
    }
  };

  const handleCancel = async (c) => {
    if (!window.confirm(`Cancel campaign "${c.name}"?`)) return;
    try {
      await cancelEmailCampaign(orgId, c.id);
      showToast('Campaign cancelled.');
      loadCampaigns();
    } catch (err) {
      showToast('Cancel failed: ' + err.message, 'error');
    }
  };

  // ── Filtered & paginated ──────────────────────────────────────────────────
  const filtered = campaigns.filter(c => {
    if (statusFilter && c.status !== statusFilter) return false;
    if (search && !(c.name || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Template CRUD ─────────────────────────────────────────────────────────
  const handleTplSave = async (data, existing) => {
    setTplSaving(true);
    try {
      if (existing) {
        await updateEmailTemplate(orgId, existing.id, data);
      } else {
        await createEmailTemplate(orgId, data, adminId);
      }
      showToast('Template saved.');
      setTplEditingId(null);
      loadTemplates();
    } catch (err) {
      showToast('Save failed: ' + err.message, 'error');
    } finally {
      setTplSaving(false);
    }
  };

  const handleTplDelete = async (tpl) => {
    setTplDeleting(true);
    try {
      await deleteEmailTemplate(orgId, tpl.id);
      showToast('Template deleted.');
      setTplDeleteConfirm(null);
      loadTemplates();
    } catch (err) {
      showToast('Delete failed: ' + err.message, 'error');
    } finally {
      setTplDeleting(false);
    }
  };

  // ── Opt-In management ─────────────────────────────────────────────────────
  const filteredOptIn = optInContacts.filter(c => {
    const optIn = getContactOptIn(c, 'email');
    if (optInFilter === 'opted_in' && !optIn) return false;
    if (optInFilter === 'opted_out' && optIn) return false;
    if (optInSearch) {
      const q = optInSearch.toLowerCase();
      return (c.displayName || c.name || '').toLowerCase().includes(q) ||
             (c.email || '').toLowerCase().includes(q);
    }
    return true;
  });
  const optInTotalPages = Math.max(1, Math.ceil(filteredOptIn.length / PAGE_SIZE));
  const pagedOptIn = filteredOptIn.slice((optInPage - 1) * PAGE_SIZE, optInPage * PAGE_SIZE);

  const handleOptInUpdate = async (contact, newOptIn) => {
    if (!optInReason.trim()) { showToast('Please enter a reason before updating.', 'error'); return; }
    setUpdatingOptIn(contact.id);
    try {
      await updateChannelOptIn(orgId, contact.id, contact._collection, 'email', newOptIn, adminId, optInReason);
      showToast(`Email opt-in updated for ${contact.displayName || contact.name}.`);
      setShowOptInReasonFor(null);
      setOptInReason('');
      loadOptInContacts();
    } catch (err) {
      showToast('Update failed: ' + err.message, 'error');
    } finally {
      setUpdatingOptIn(null);
    }
  };

  const handleExportOptInCSV = () => {
    const csv = exportChannelOptInCSV(filteredOptIn, 'email');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = 'email-optin-list.csv'; link.click();
    URL.revokeObjectURL(url);
  };

  // ── Filtered templates ────────────────────────────────────────────────────
  const filteredTemplates = tplCategory === 'all'
    ? templates
    : templates.filter(t => (t.category || 'triggered') === tplCategory);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="bb-comm-root">

      {/* Sub-tab bar */}
      <div className="bb-comm-subtab-bar">
        {[
          { key: 'create',     label: editingId ? 'Edit Campaign' : 'Create Campaign' },
          { key: 'templates',  label: 'Templates' },
          { key: 'optin',      label: 'Opt-In Management' },
          { key: 'frequency',  label: 'Campaign Frequency' },
        ].map(t => (
          <button
            key={t.key}
            className={`bb-comm-subtab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => { if (t.key !== 'create') resetForm(); setActiveTab(t.key); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── (Campaigns listing removed) ─────────────────────────────────── */}
      {false && (
        <div>
          <div className="bb-comm-section-header">
            <h3 className="bb-comm-section-title">Email Campaigns</h3>
          </div>

          <div className="bb-comm-filters-row">
            <input
              className="bb-form-input bb-comm-search"
              placeholder="Search campaigns…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
            <select
              className="bb-form-select bb-comm-filter-select"
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="sent">Sent</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {loadingCampaigns ? (
            <div className="bb-comm-loading">Loading campaigns…</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="bb-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Campaign Name</th>
                      <th>Template</th>
                      <th>Audience</th>
                      <th>Status</th>
                      <th>Recipients</th>
                      <th>Sent / Scheduled</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="bb-comm-empty-row">
                          {(statusFilter || search) && filtered.length === 0
                            ? 'No campaigns match your filters.'
                            : 'No email campaigns yet. Create your first campaign.'}
                        </td>
                      </tr>
                    ) : paged.map(c => {
                      const tpl = templates.find(t => t.id === c.templateId);
                      return (
                        <tr key={c.id}>
                          <td className="bb-comm-cell--name">{c.name}</td>
                          <td className="bb-comm-cell--muted">{tpl ? tpl.name : '—'}</td>
                          <td><AudienceLabel audienceType={c.audienceType} /></td>
                          <td><StatusBadge status={c.status} /></td>
                          <td>{c.recipientCount || '—'}</td>
                          <td className="bb-comm-cell--muted">
                            {c.sentAt ? fmtDate(c.sentAt) : c.scheduledAt ? fmtDateTime(c.scheduledAt) : '—'}
                          </td>
                          <td className="bb-comm-cell--actions">
                            <button className="bb-comm-action-btn" onClick={() => setViewCampaign(c)}>View</button>
                            {' '}
                            {(c.status === 'draft' || c.status === 'scheduled') && (
                              <><button className="bb-comm-action-btn" onClick={() => handleEdit(c)}>Edit</button>{' '}</>
                            )}
                            <button className="bb-comm-action-btn" onClick={() => handleDuplicate(c)}>Duplicate</button>
                            {' '}
                            {(c.status === 'draft' || c.status === 'scheduled') && (
                              <button className="bb-comm-action-btn bb-comm-action-btn--danger" onClick={() => handleCancel(c)}>Cancel</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 16, fontSize: 13 }}>
                  <button className="bb-btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
                  <span style={{ color: '#6B7280' }}>Page {page} of {totalPages}</span>
                  <button className="bb-btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── CREATE / EDIT TAB ───────────────────────────────────────────── */}
      {activeTab === 'create' && (
        <div className="bb-comm-campaign-form">
          <div className="bb-comm-section-header" style={{ marginBottom: 20 }}>
            <h3 className="bb-comm-section-title">{editingId ? 'Edit Email Campaign' : 'Create Email Campaign'}</h3>
          </div>

          {/* Campaign Details */}
          <div className="bb-comm-section-card">
            <div className="bb-comm-card-title">Campaign Details</div>
            <div className="bb-form-group">
              <label className="bb-form-label">Campaign Name *</label>
              <input
                className="bb-form-input"
                value={form.name}
                onChange={e => setF('name', e.target.value)}
                placeholder="e.g. Monthly Member Update — May 2026"
              />
            </div>
            <div className="bb-form-group">
              <label className="bb-form-label">Start from Template <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span></label>
              <select className="bb-form-select" value={form.templateId} onChange={e => handleTemplateSelect(e.target.value)}>
                <option value="">— Select a template —</option>
                <optgroup label="Triggered Campaigns">
                  {templates.filter(t => (t.category || 'triggered') === 'triggered').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </optgroup>
                <optgroup label="AdHoc Campaigns">
                  {templates.filter(t => t.category === 'adhoc').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </optgroup>
              </select>
            </div>
          </div>

          {/* Email Content */}
          <div className="bb-comm-section-card">
            <div className="bb-comm-card-title">Email Content</div>
            <div className="bb-form-group">
              <label className="bb-form-label">Subject Line *</label>
              <input
                className="bb-form-input"
                value={form.subject}
                onChange={e => setF('subject', e.target.value)}
                placeholder="e.g. An Important Update from {{orgName}}"
              />
            </div>
            <div className="bb-form-group">
              <label className="bb-form-label">Email Body *</label>
              <div className="bb-comm-merge-fields">
                <span style={{ fontSize: 11, color: '#9CA3AF', marginRight: 4 }}>Insert:</span>
                {MERGE_FIELDS.filter(f => f.tag === '{{firstName}}' || f.tag === '{{fullName}}').map(f => (
                  <button key={f.tag} className="bb-comm-merge-btn" type="button" onClick={() => insertMergeField(f.tag)}>
                    {f.label}
                  </button>
                ))}
                {/* Insert Image button */}
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <button
                    type="button"
                    className="bb-comm-merge-btn"
                    style={{ background: showImageInsert ? '#EEF2FF' : undefined, borderColor: showImageInsert ? '#4338CA' : undefined }}
                    onClick={() => { setShowImageInsert(v => !v); setImageInsertUrl(''); }}
                  >
                    🖼 Image
                  </button>
                  {showImageInsert && (
                    <div style={{
                      position: 'absolute', top: '110%', left: 0, zIndex: 200,
                      background: '#f8f9fa', border: '1px solid #E5E7EB', borderRadius: 8,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: 12, width: 340,
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Image URL (or GIF URL):</div>
                      <input
                        type="url"
                        className="bb-form-input"
                        style={{ fontSize: 13, marginBottom: 8 }}
                        placeholder="https://example.com/image.jpg"
                        value={imageInsertUrl}
                        onChange={e => setImageInsertUrl(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleInsertImage(); } }}
                        autoFocus
                      />
                      {imageInsertUrl.trim() && (
                        <div style={{ marginBottom: 8, borderRadius: 6, overflow: 'hidden', background: '#F3F4F6', lineHeight: 0 }}>
                          <img
                            src={imageInsertUrl.trim()}
                            alt="Preview"
                            style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                            onError={e => { e.currentTarget.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <button type="button" className="bb-btn-primary" style={{ fontSize: 13, padding: '5px 14px' }} onClick={handleInsertImage} disabled={!imageInsertUrl.trim()}>
                          Insert
                        </button>
                        <button type="button" className="bb-btn-secondary" style={{ fontSize: 13, padding: '5px 14px' }} onClick={() => setShowImageInsert(false)}>
                          Cancel
                        </button>
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        Tip: You can use image hosting services like Imgur, Cloudinary, or your own server. GIFs are also supported!
                      </div>
                    </div>
                  )}
                </div>
                <EmojiPicker onSelect={e => setF('body', form.body + e)} />
              </div>
              <textarea
                ref={bodyRef}
                className="bb-form-input"
                style={{ minHeight: 200, fontFamily: 'inherit', fontSize: 13, marginTop: 6 }}
                value={form.body}
                onChange={e => setF('body', e.target.value)}
                placeholder="Write your email body here. Use the merge field buttons above to personalise, or click 🖼 Image to embed images at any position."
              />
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                Images are embedded inline using <code style={{ background: '#F3F4F6', borderRadius: 3, padding: '1px 4px' }}>[IMAGE:url]</code> markers. Use the 🖼 Image button to insert them at the cursor.
              </div>
            </div>
            <MessagePreviewPane
              subject={form.subject}
              body={form.body}
              orgName={previewOrgName}
              previewName={previewStaffName}
              customMergeValues={form.customMergeValues}
            />
          </div>

          {/* Merge Field Values */}
          <MergeFieldValuesSection
            values={form.customMergeValues || {}}
            onChange={vals => setF('customMergeValues', vals)}
            body={(form.subject || '') + ' ' + (form.body || '')}
          />

          {/* Audience & Targeting */}
          <div className="bb-comm-section-card">
            <div className="bb-comm-card-title">Audience & Targeting</div>
            <AudienceFiltersSection
              targetingType={targetingType}
              onTargetingTypeChange={t => { setTargetingType(t); setSelectedContacts([]); setContactSearch(''); setContactResults([]); }}
              audienceFilters={form.audienceFilters}
              onFiltersChange={v => setF('audienceFilters', v)}
              contactSearch={contactSearch}
              setContactSearch={setContactSearch}
              contactResults={contactResults}
              searchingContacts={searchingContacts}
              selectedContacts={selectedContacts}
              onAddContact={handleAddContact}
              onRemoveContact={handleRemoveContact}
              memberStatuses={memberStatuses}
              memberDistricts={memberDistricts}
              volunteerStatuses={volunteerStatuses}
              volunteerDistricts={volunteerDistricts}
              templateAudience={templateAudience}
            />

            {/* Save Targeting Criteria */}
            {(() => {
              const currentKey = JSON.stringify({ targetingType, audienceFilters: form.audienceFilters, selectedContacts });
              const savedKey = savedTargeting ? JSON.stringify({ targetingType: savedTargeting.targetingType, audienceFilters: savedTargeting.audienceFilters, selectedContacts: savedTargeting.selectedContacts }) : null;
              const isDirty = savedTargeting && currentKey !== savedKey;
              return (
                <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="bb-btn-primary"
                    style={{ fontSize: 13, padding: '7px 18px' }}
                    onClick={() => {
                      setSavedTargeting({ targetingType, audienceFilters: form.audienceFilters, selectedContacts });
                    }}
                  >
                    💾 Save Targeting Criteria
                  </button>
                  {savedTargeting && !isDirty && (
                    <span style={{ fontSize: 12, color: '#065F46', background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 12, padding: '3px 10px', fontWeight: 600 }}>
                      ✓ Criteria saved
                    </span>
                  )}
                  {isDirty && (
                    <span style={{ fontSize: 12, color: '#92400E', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 12, padding: '3px 10px', fontWeight: 600 }}>
                      ⚠ Modified — save again to update
                    </span>
                  )}
                </div>
              );
            })()}

            {/* Audience summary */}
            <div className="bb-comm-audience-summary" style={{ marginTop: 12 }}>
              {audienceLoading ? (
                <span style={{ color: '#9CA3AF' }}>Calculating audience…</span>
              ) : audienceResult !== null ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <span style={{ fontWeight: 700, color: '#4338CA', fontSize: 18 }}>{audienceResult.eligibleCount}</span>
                    <span style={{ fontSize: 13, color: '#374151', marginLeft: 6 }}>eligible recipients</span>
                  </div>
                  {audienceResult.eligibleCount > 0 && savedTargeting && (
                    <button
                      className="bb-btn-secondary"
                      style={{ fontSize: 12, padding: '4px 12px' }}
                      onClick={async () => {
                        await refreshAudience(derivedAudienceType(), form.audienceFilters);
                        setShowRecipientPreview(true);
                      }}
                    >
                      👁 Preview Recipients
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {/* Send Options */}
          <div className="bb-comm-section-card">
            <div className="bb-comm-card-title">Send Options</div>
            <div className="bb-comm-radio-group" style={{ marginBottom: 12 }}>
              <label className="bb-comm-schedule-option">
                <input type="radio" name="scheduleType" value="now" checked={form.scheduleType === 'now'} onChange={() => setF('scheduleType', 'now')} />
                Send Now
              </label>
              <label className="bb-comm-schedule-option">
                <input type="radio" name="scheduleType" value="schedule" checked={form.scheduleType === 'schedule'} onChange={() => setF('scheduleType', 'schedule')} />
                Schedule for Later
              </label>
              <label className="bb-comm-schedule-option">
                <input type="radio" name="scheduleType" value="recurring" checked={form.scheduleType === 'recurring'} onChange={() => setF('scheduleType', 'recurring')} />
                Recurring
              </label>
            </div>
            {form.scheduleType === 'schedule' && (
              <div className="bb-form-group">
                <label className="bb-form-label">Scheduled Date & Time <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(Singapore Time)</span></label>
                <input
                  type="datetime-local"
                  className="bb-form-input"
                  style={{ maxWidth: 280 }}
                  value={form.scheduledAt}
                  onChange={e => setF('scheduledAt', e.target.value)}
                />
              </div>
            )}
            {form.scheduleType === 'recurring' && (
              <RecurringSection recurring={form.recurring} onChange={v => setF('recurring', v)} />
            )}
          </div>

          {/* Final Customers Targeted */}
          <FinalCustomersTargeted
            orgId={orgId}
            campaignType="email"
            formData={savedTargeting ? { ...form, targetingType: savedTargeting.targetingType, audienceFilters: savedTargeting.audienceFilters, selectedContacts: savedTargeting.selectedContacts } : form}
            disabled={!savedTargeting}
          />

          {/* Actions */}
          <div className="bb-comm-form-actions">
            <button className="bb-btn-secondary" onClick={handleSaveDraft} disabled={formSaving}>
              {formSaving ? 'Saving…' : 'Save Draft'}
            </button>
            <button
              className="bb-btn-primary"
              onClick={() => handleOpenSend(form.scheduleType === 'schedule' || form.scheduleType === 'recurring' ? 'schedule' : 'now')}
              disabled={formSaving}
            >
              {form.scheduleType === 'schedule' || form.scheduleType === 'recurring' ? 'Schedule Campaign' : 'Send Campaign'}
            </button>
            <button className="bb-btn-ghost" type="button" onClick={() => { resetForm(); setActiveTab('create'); }}>
              Discard
            </button>
          </div>
        </div>
      )}

      {/* ─── TEMPLATES TAB ───────────────────────────────────────────────── */}
      {activeTab === 'templates' && (
        <div>
          <div className="bb-comm-section-header">
            <h3 className="bb-comm-section-title">Email Templates</h3>
            <button className="bb-btn-primary" onClick={() => setTplEditingId('new')}>+ New Template</button>
          </div>

          {/* Campaign type filter tabs */}
          <div className="bb-comm-category-tabs" style={{ marginBottom: 16 }}>
            {[
              { key: 'all',       label: `All (${templates.length})` },
              { key: 'triggered', label: `Triggered Campaigns (${templates.filter(t => (t.category || 'triggered') === 'triggered').length})` },
              { key: 'adhoc',     label: `AdHoc Campaigns (${templates.filter(t => t.category === 'adhoc').length})` },
            ].map(c => (
              <button
                key={c.key}
                className={`bb-comm-category-tab${tplCategory === c.key ? ' active' : ''}`}
                onClick={() => setTplCategory(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Campaign type descriptions */}
          <div style={{ marginBottom: 14, fontSize: 13, color: '#6B7280' }}>
            {tplCategory === 'triggered' && 'Triggered Campaign templates are automatically sent in response to specific member or volunteer actions (e.g. registration, event sign-up).'}
            {tplCategory === 'adhoc' && 'AdHoc Campaign templates are used for one-off outreach campaigns (e.g. monthly event newsletters, volunteer recruitment for a specific event).'}
          </div>

          {tplEditingId === 'new' && (
            <div className="bb-comm-card" style={{ marginBottom: 16 }}>
              <div className="bb-comm-card-title">Create New Template</div>
              <TemplateEditorForm
                initial={null}
                onSave={data => handleTplSave(data, null)}
                onCancel={() => setTplEditingId(null)}
                saving={tplSaving}
              />
            </div>
          )}

          {filteredTemplates.length === 0 ? (
            <div className="bb-comm-loading">No templates found{tplCategory !== 'all' ? ` in this category` : ''}.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="bb-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Template Name</th>
                    <th>Category</th>
                    <th>Trigger Context</th>
                    <th>Audience</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTemplates.map(tpl => (
                    <React.Fragment key={tpl.id}>
                      <tr className={tplEditingId === tpl.id ? 'bb-comm-row--editing' : ''}>
                        <td className="bb-comm-cell--name">
                          {tpl.name}
                          {tpl.isDefault && <span className="bb-comm-badge bb-comm-badge--default">Default</span>}
                        </td>
                        <td>
                          <span className={`bb-comm-category-badge bb-comm-category-badge--${tpl.category || 'triggered'}`}>
                            {tpl.category === 'adhoc' ? 'AdHoc' : 'Triggered'}
                          </span>
                        </td>
                        <td className="bb-comm-cell--muted">{tpl.triggerContext || '—'}</td>
                        <td><AudienceLabel audienceType={tpl.audience} /></td>
                        <td className="bb-comm-cell--actions">
                          <button
                            className="bb-comm-action-btn"
                            onClick={() => {
                              setForm(f => ({ ...f, templateId: tpl.id, subject: tpl.subject || f.subject, body: tpl.body || f.body }));
                              setActiveTab('create');
                            }}
                          >
                            Use
                          </button>
                          {' '}
                          <button className="bb-comm-action-btn" onClick={() => setTplEditingId(tplEditingId === tpl.id ? null : tpl.id)}>
                            {tplEditingId === tpl.id ? 'Cancel Edit' : 'Edit'}
                          </button>
                          {' '}
                          {tplDeleteConfirm === tpl.id ? (
                            <span className="bb-comm-inline-confirm">
                              Delete?{' '}
                              <button className="bb-comm-action-btn bb-comm-action-btn--danger" onClick={() => handleTplDelete(tpl)} disabled={tplDeleting}>
                                {tplDeleting ? '…' : 'Yes'}
                              </button>{' '}
                              <button className="bb-comm-action-btn" onClick={() => setTplDeleteConfirm(null)}>No</button>
                            </span>
                          ) : (
                            !tpl.isDefault && (
                              <button className="bb-comm-action-btn bb-comm-action-btn--danger" onClick={() => setTplDeleteConfirm(tpl.id)}>
                                Delete
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                      {tplEditingId === tpl.id && (
                        <tr>
                          <td colSpan={5} className="bb-comm-cell--editor">
                            <TemplateEditorForm
                              initial={tpl}
                              onSave={data => handleTplSave(data, tpl)}
                              onCancel={() => setTplEditingId(null)}
                              saving={tplSaving}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── OPT-IN MANAGEMENT TAB ───────────────────────────────────────── */}
      {activeTab === 'optin' && (
        <div style={{ padding: '24px 0' }}>
          <div className="bb-comm-section-header" style={{ marginBottom: 8 }}>
            <h3 className="bb-comm-section-title">Email Opt-In Management</h3>
            <button className="bb-btn-secondary" onClick={handleExportOptInCSV} disabled={filteredOptIn.length === 0}>
              Export CSV
            </button>
          </div>
          <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 16 }}>
            Members and volunteers manage their email subscription from their profile. Admins can manually override opt-in status here with a recorded reason for audit compliance.
          </p>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <input
              className="bb-form-input"
              style={{ maxWidth: 280 }}
              placeholder="Search by name or email…"
              value={optInSearch}
              onChange={e => { setOptInSearch(e.target.value); setOptInPage(1); }}
            />
            <select
              className="bb-form-input"
              style={{ maxWidth: 180 }}
              value={optInFilter}
              onChange={e => { setOptInFilter(e.target.value); setOptInPage(1); }}
            >
              <option value="">All Contacts</option>
              <option value="opted_in">Opted In</option>
              <option value="opted_out">Opted Out</option>
            </select>
          </div>
          {loadingOptIn ? (
            <div className="bb-comm-loading">Loading contacts…</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="bb-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Email</th>
                      <th>Opt-In Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedOptIn.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="bb-comm-empty-row">
                          {optInContacts.length === 0 ? 'No contacts found.' : 'No contacts match your filters.'}
                        </td>
                      </tr>
                    ) : pagedOptIn.map(contact => {
                      const isOptedIn = getContactOptIn(contact, 'email');
                      return (
                        <React.Fragment key={contact.id}>
                          <tr>
                            <td className="bb-comm-cell--name">{contact.displayName || contact.name || '—'}</td>
                            <td><span style={{ textTransform: 'capitalize', fontSize: 12, color: '#6B7280' }}>{contact._type}</span></td>
                            <td className="bb-comm-cell--muted">{contact.email || '—'}</td>
                            <td>
                              <span style={{
                                display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                                fontSize: 11, fontWeight: 700,
                                ...(isOptedIn
                                  ? { color: '#065F46', border: '1px solid #6EE7B7', background: '#D1FAE5' }
                                  : { color: '#6B7280', border: '1px solid #D1D5DB', background: '#F3F4F6' }),
                              }}>
                                {isOptedIn ? 'Opted In' : 'Opted Out'}
                              </span>
                            </td>
                            <td className="bb-comm-cell--actions">
                              <button
                                className="bb-comm-action-btn"
                                onClick={() => {
                                  setShowOptInReasonFor(contact.id === showOptInReasonFor ? null : contact.id);
                                  setOptInReason('');
                                }}
                              >
                                {showOptInReasonFor === contact.id ? 'Cancel' : 'Change Status'}
                              </button>
                            </td>
                          </tr>
                          {showOptInReasonFor === contact.id && (
                            <tr>
                              <td colSpan={5} style={{ padding: '12px 16px', background: '#F9FAFB' }}>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                  <div className="bb-form-group" style={{ flex: 1, minWidth: 240, marginBottom: 0 }}>
                                    <label className="bb-form-label">Reason for change *</label>
                                    <input
                                      className="bb-form-input"
                                      value={optInReason}
                                      onChange={e => setOptInReason(e.target.value)}
                                      placeholder="e.g. Contact requested opt-out by phone"
                                    />
                                  </div>
                                  {isOptedIn ? (
                                    <button
                                      className="bb-comm-action-btn bb-comm-action-btn--danger"
                                      onClick={() => handleOptInUpdate(contact, false)}
                                      disabled={updatingOptIn === contact.id || !optInReason.trim()}
                                    >
                                      {updatingOptIn === contact.id ? '…' : 'Opt Out'}
                                    </button>
                                  ) : (
                                    <button
                                      className="bb-comm-action-btn"
                                      style={{ background: '#D1FAE5', color: '#065F46', borderColor: '#6EE7B7' }}
                                      onClick={() => handleOptInUpdate(contact, true)}
                                      disabled={updatingOptIn === contact.id || !optInReason.trim()}
                                    >
                                      {updatingOptIn === contact.id ? '…' : 'Opt In'}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                <button className="bb-btn-secondary" onClick={() => setOptInPage(p => Math.max(1, p - 1))} disabled={optInPage === 1}>Previous</button>
                <span style={{ color: '#6B7280' }}>Page {optInPage} of {optInTotalPages} ({filteredOptIn.length} contacts)</span>
                <button className="bb-btn-secondary" onClick={() => setOptInPage(p => Math.min(optInTotalPages, p + 1))} disabled={optInPage === optInTotalPages}>Next</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─── CAMPAIGN FREQUENCY TAB ──────────────────────────────────────── */}
      {activeTab === 'frequency' && (
        <div style={{ maxWidth: 700, padding: '24px 0' }}>
          <div className="bb-comm-section-header" style={{ marginBottom: 20 }}>
            <h3 className="bb-comm-section-title">Campaign Frequency</h3>
          </div>
          <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 20 }}>
            Control how often contacts can receive email campaigns to prevent over-communication
            and maintain healthy engagement rates.
          </p>
          <CampaignFrequencyControl orgId={orgId} campaignType="email" />
        </div>
      )}

      {/* ─── Modals ──────────────────────────────────────────────────────── */}
      {showRecipientPreview && (
        <RecipientPreviewModal
          isOpen={showRecipientPreview}
          onClose={() => setShowRecipientPreview(false)}
          previewData={audienceResult}
          campaignName={form.name}
        />
      )}

      {showSendConfirm && (
        <SendConfirmModal
          name={form.name}
          subject={form.subject}
          recipientCount={audienceResult?.eligibleCount ?? 0}
          scheduleMode={sendAction === 'schedule'}
          scheduleAt={form.scheduledAt}
          onConfirm={handleConfirmSend}
          onClose={() => setShowSendConfirm(false)}
          sending={sendingCampaign}
        />
      )}

      {viewCampaign && <ViewModal campaign={viewCampaign} onClose={() => setViewCampaign(null)} />}

      {/* Toast */}
      {toast && (
        <div className={`bb-comm-toast bb-comm-toast--${toast.type}`} onClick={() => setToast(null)}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
